import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import { DebugPanel } from '@rsos/debug-panel';
import { useSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { MockTransferControls } from './MockTransferControls';
import { SimulateCallControls } from './SimulateCallControls';
import { SimulateTt911LocationControls } from './SimulateTt911LocationControls';
import { TcapiCredentialSetup } from './TcapiCredentialSetup';
import {
  StyledDescription,
  StyledInputWrapper,
} from './TextTo911DebugPanel.styles';

interface TextTo911DebugPanelProps {
  onClose?: () => void;
}

function TextTo911DebugPanel({ onClose }: TextTo911DebugPanelProps) {
  const DEFAULT_PHONE_NUMBER = '15555555555';
  const [phoneNumber, setPhoneNumber] = useState(DEFAULT_PHONE_NUMBER);

  const selectedCallerID = useSelectedCallerID();
  const conversation = useSelector(
    (state: {
      conversations: Record<
        string,
        { source: string; data: { session_id?: string } }
      >;
    }) => (selectedCallerID ? state.conversations[selectedCallerID] : null),
  );

  // sessionId on a TCAPI conversation is the TCAPI callId
  const activeCallId =
    selectedCallerID && conversation?.source === SOURCE_TCAPI
      ? (conversation.data?.session_id ?? null)
      : null;

  // as the user selects calls, update the debug panel to use that call by default
  useEffect(() => {
    if (selectedCallerID) {
      setPhoneNumber(selectedCallerID);
    }
  }, [selectedCallerID]);

  return (
    <DebugPanel title="Simulate Text-to-911 Session" onClose={onClose}>
      <StyledDescription>
        Simulates an inbound Text-to-911 call by posting a location to ELS and
        triggering Shanghai&apos;s mock TCAPI WebSocket server to send a{' '}
        <code>callPresented</code> event to the text_to_911 service. This
        creates a real database session so Accept/End Chat buttons work.
        Currently only supports TCAPI sessions. Future work could add support
        for SMS (Twilio-based) and RCS session types.
      </StyledDescription>
      <TcapiCredentialSetup />
      <StyledInputWrapper>
        <InputText
          type="text"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setPhoneNumber(event.target.value)
          }
          value={phoneNumber}
        />
      </StyledInputWrapper>
      <SimulateTt911LocationControls phoneNumber={phoneNumber} />
      <SimulateCallControls phoneNumber={phoneNumber} callId={activeCallId} />
      {selectedCallerID && <MockTransferControls callerID={selectedCallerID} />}
    </DebugPanel>
  );
}

export default TextTo911DebugPanel;
